import { useEffect, useState } from 'react';

// Response shape from GET /api/health (see keyforge/src/kf_handler_health.sk
// and the mock backend for reference values).
export interface HealthResponse {
  ok: boolean;
  version?: string;
  uptimeMs?: number;
}

type Status =
  | { kind: 'unknown' }
  | { kind: 'ok'; version?: string; uptimeMs?: number }
  | { kind: 'down'; reason: string };

interface Props {
  // Milliseconds between polls. Default 60s so we don't hammer /api/health
  // — the endpoint is unauthenticated + cheap, but rendering churn adds up.
  // Tests override to speed up.
  pollIntervalMs?: number;
}

const DEFAULT_POLL_MS = 60_000;

async function probeOnce(): Promise<Status> {
  try {
    const res = await fetch('/api/health', { headers: { accept: 'application/json' } });
    if (!res.ok) return { kind: 'down', reason: `HTTP ${res.status}` };
    const json = (await res.json()) as HealthResponse;
    if (!json.ok) return { kind: 'down', reason: 'server reported not-ok' };
    return { kind: 'ok', version: json.version, uptimeMs: json.uptimeMs };
  } catch (e) {
    return { kind: 'down', reason: (e as Error).message };
  }
}

export function BackendHealth({ pollIntervalMs = DEFAULT_POLL_MS }: Props) {
  const [status, setStatus] = useState<Status>({ kind: 'unknown' });

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const tick = async () => {
      const next = await probeOnce();
      if (cancelled) return;
      setStatus(next);
      timer = setTimeout(tick, pollIntervalMs);
    };

    void tick();
    return () => {
      cancelled = true;
      if (timer !== null) clearTimeout(timer);
    };
  }, [pollIntervalMs]);

  if (status.kind === 'unknown') {
    return <span className="backend-health backend-health-unknown" aria-label="Backend status: checking">•</span>;
  }
  if (status.kind === 'down') {
    return (
      <span
        className="backend-health backend-health-down"
        aria-label="Backend status: down"
        title={`Backend unreachable: ${status.reason}`}
      >
        •
      </span>
    );
  }
  const title = status.version
    ? `Backend OK · ${status.version}`
    : 'Backend OK';
  return (
    <span
      className="backend-health backend-health-ok"
      aria-label="Backend status: healthy"
      title={title}
    >
      •
    </span>
  );
}
